import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, PackageSearch, Wrench } from "lucide-react";
import { api, DOMAINES, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Chantier() {
  const { siteId } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    api
      .get(`/sites/${siteId}/equipements`)
      .then(({ data }) => setData(data))
      .catch((e) => setErreur(errMsg(e, "Chantier introuvable")));
  }, [siteId]);

  const lien = (m) =>
    `/declarer/${m.domaine}?site=${siteId}&materiel=${encodeURIComponent(m.designation)}${m.id ? `&materiel_id=${m.id}` : ""}&qr=1`;

  return (
    <div className="space-y-7" data-testid="chantier-page">
      <div>
        <div className="bk-label mb-2 flex items-center gap-2">
          <MapPin size={14} /> {params.get("qr") ? "QR chantier scanné" : "Chantier"}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
          {data?.site?.nom || "Chargement…"}
        </h1>
        {data?.site?.adresse && <p className="text-sm text-slate-600 mt-2">{data.site.adresse}</p>}
        <p className="text-sm text-slate-600 mt-2">Sélectionnez l'équipement concerné pour lancer le diagnostic guidé.</p>
      </div>

      {erreur && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700" data-testid="chantier-erreur">
          {erreur}
        </div>
      )}

      {data?.equipements?.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center" data-testid="chantier-vide">
          <PackageSearch size={26} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucun équipement référencé sur ce chantier. Choisissez un domaine ci-dessous.</p>
        </div>
      )}

      <div className="space-y-3">
        {data?.equipements?.map((m, i) => (
          <motion.div key={m.id || `${m.designation}-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Link
              to={lien(m)}
              data-testid={`chantier-equip-${m.id || m.designation}`}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 hover:border-[#005CFF] transition-colors"
            >
              <Wrench size={18} className="text-[#005CFF] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{m.designation}</div>
                <div className="text-xs text-slate-500 truncate">
                  {m.domaine_label}
                  {m.numero_serie ? ` · SN ${m.numero_serie}` : ""}
                  {m.date_pose ? ` · posé le ${m.date_pose}` : ""}
                </div>
              </div>
              <ArrowRight size={16} className="text-slate-400 shrink-0" />
            </Link>
          </motion.div>
        ))}
      </div>

      <div>
        <div className="bk-label mb-3">Autre demande sur ce chantier</div>
        <div className="flex flex-wrap gap-2">
          {DOMAINES.map((d) => (
            <Button
              key={d.key}
              data-testid={`chantier-domaine-${d.key}`}
              variant="outline"
              size="sm"
              asChild
            >
              <Link to={`/declarer/${d.key}?site=${siteId}&qr=1`}>{d.label}</Link>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
